import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  Feature,
  FeatureStatus,
} from '../../models/feature-management.models';
import { FeatureManagementService } from '../../services/feature-management.service';
import { FeatureFlagService, ToastService } from '@overtur-monorepo/keying-shared';
import { User, UserService } from '@overtur-monorepo/keying-user';
import { ConfirmationDialogComponent, ConfirmationDialogData } from '../confirmation-dialog/confirmation-dialog.component';

export interface UserTarget {
  userId: number;
  email: string;
}

@Component({
  selector: 'app-feature-editor',
  templateUrl: './feature-editor.component.html',
  styleUrls: ['./feature-editor.component.scss'],
  standalone: false,
})
export class FeatureEditorComponent implements OnInit, OnDestroy {
  featureForm!: FormGroup;
  loading = false;
  isEditMode = false;
  featureId: number | null = null;
  currentVersion = 1; // Track version for optimistic concurrency

  allUsers: User[] = [];

  statuses: FeatureStatus[] = ['Disabled', 'Limited Access', 'Full Access'];
  categories: string[] = [];
  specificUsers: UserTarget[] = [];
  roleRules: { role: string; priority: number }[] = [];
  selectedUserId: number | null = null;
  pilotValidationError = false;
  userFeedbackMessage = '';
  userFeedbackType: 'success' | 'warning' | 'error' | 'info' | '' = '';
  roleFeedbackMessage = '';
  roleFeedbackType: 'success' | 'warning' | 'error' | 'info' | '' = '';

  // Collapsible section states
  isUsersExpanded = false;
  isRolesExpanded = false;

  private destroy$ = new Subject<void>();
  private initialFormState: any = null; // Track original form state for dirty checking

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private featureService: FeatureManagementService,
    private toastService: ToastService,
    private userService: UserService,
    private dialog: MatDialog,
    private featureFlagService: FeatureFlagService
  ) {}

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    if (idParam && idParam !== 'new') {
      this.featureId = Number(idParam);
      this.isEditMode = true;
    } else {
      this.featureId = null;
      this.isEditMode = false;
    }

    this.initForm();
    this.loadCategories();
    this.loadUsers();

    if (this.isEditMode && this.featureId) {
      this.loadFeature(this.featureId);
    }

    // Handle status changes
    this.featureForm.get('status')?.valueChanges.subscribe((status) => {
      if (status !== 'Limited Access') {
        this.pilotValidationError = false;

        // Collapse both sections when not in Limited Access mode
        this.isRolesExpanded = false;
        this.isUsersExpanded = false;
      } else {
        // Expand both sections when switching to Limited Access mode
        this.isRolesExpanded = true;
        this.isUsersExpanded = true;

        // Auto-add SuperUser role when switching to Limited Access mode if no targeting rules exist
        if (this.roleRules.length === 0 && this.specificUsers.length === 0) {
          this.roleRules.push({
            role: 'SuperUser',
            priority: 100
          });
          this.pilotValidationError = false;
        }
      }
    });
  }

  loadUsers(): void {
    // Fetch users from API
    this.userService.getAllUsers();

    // Subscribe to users observable
    this.userService.users$
      .pipe(takeUntil(this.destroy$))
      .subscribe((users: User[]) => {
        this.allUsers = users.filter(u => `${u.firstName} ${u.lastName}` !== 'External User');
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initForm(): void {
    this.featureForm = this.fb.group({
      key: [''], // Hidden field, auto-generated from name
      name: ['', Validators.required],
      description: ['', Validators.required],
      category: ['', Validators.required],
      status: ['Disabled', Validators.required],
      rolloutPercentage: [100, [Validators.required, Validators.min(0), Validators.max(100)]], // Default 100% (UI hidden, not using gradual rollout)
    });

    // Auto-generate key from name (only in create mode)
    if (!this.isEditMode) {
      this.featureForm.get('name')?.valueChanges.subscribe((name: string) => {
        if (name) {
          const generatedKey = this.generateKeyFromName(name);
          this.featureForm.patchValue({ key: generatedKey }, { emitEvent: false });
        }
      });

      // Capture initial state for new features
      this.initialFormState = this.captureFormState();
    }
  }


  loadCategories(): void {
    // TODO: Enable when backend endpoint is implemented
    // For now, use hardcoded categories
    this.categories = ['UI', 'API', 'Integration', 'Security', 'Performance', 'Other'];

    // this.featureService
    //   .getCategories()
    //   .pipe(takeUntil(this.destroy$))
    //   .subscribe({
    //     next: (categories) => {
    //       this.categories = categories;
    //     },
    //     error: (error) => {
    //       console.error('Error loading categories:', error);
    //     },
    //   });
  }

  loadFeature(id: number): void {
    this.loading = true;
    this.featureService
      .getFeature(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (feature) => {
          this.populateForm(feature);
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading feature:', error);
          this.loading = false;
          this.router.navigate(['/feature-management/features']);
        },
      });
  }

  populateForm(feature: Feature): void {
    // Convert statusId to status string
    const statusMap: Record<number, FeatureStatus> = {
      1: 'Disabled',
      2: 'Limited Access',
      3: 'Full Access',
    };

    // Store current version for optimistic concurrency
    this.currentVersion = feature.version || 1;

    this.featureForm.patchValue({
      key: feature.featureKey,
      name: feature.name,
      description: feature.description,
      category: feature.category,
      status: statusMap[feature.statusId] || 'Disabled',
      rolloutPercentage: feature.rolloutPercentage,
    });

    // Load specific users from targeting rules
    const userRule = feature.targetingRules?.find(rule => rule.ruleType === 'user');
    if (userRule && userRule.targetValues) {
      // targetValues is a string[] where each element is a JSON-serialized UserTarget object
      this.specificUsers = [];

      for (const targetValue of userRule.targetValues) {
        try {
          // Try to parse as UserTarget JSON object (new format)
          if (targetValue.trim().startsWith('{')) {
            const userTarget = JSON.parse(targetValue) as UserTarget;
            this.specificUsers.push(userTarget);
          } else {
            // Legacy format: plain email string
            const user = this.allUsers.find(u => u.email === targetValue);
            this.specificUsers.push({
              userId: user?.id || 0,
              email: targetValue
            });
          }
        } catch (error) {
          console.warn('Failed to parse user target value:', targetValue, error);
        }
      }
    }

    // Load role rules from targeting rules
    const roleRulesList = feature.targetingRules?.filter(rule => rule.ruleType === 'role') || [];
    this.roleRules = roleRulesList.map(rule => ({
      role: rule.targetValues.join(', '),
      priority: rule.priority
    }));

    // Keep sections collapsed by default - show summaries instead
    // Users can manually expand to view/edit details

    // Capture initial state after populating form
    this.initialFormState = this.captureFormState();
  }


  save(): void {
    if (this.featureForm.invalid) {
      this.featureForm.markAllAsTouched();
      return;
    }

    const formValue = this.featureForm.getRawValue();

    // Validation: Limited Access mode requires at least one targeting rule (role or user)
    if (formValue.status === 'Limited Access' && this.roleRules.length === 0 && this.specificUsers.length === 0) {
      this.pilotValidationError = true;
      // Scroll to the error message
      setTimeout(() => {
        const errorElement = document.querySelector('.pilot-validation-error');
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      return;
    }

    // Clear validation error if it was set
    this.pilotValidationError = false;
    this.loading = true;

    // Convert status string to statusId
    const statusIdMap: Record<FeatureStatus, number> = {
      'Disabled': 1,
      'Limited Access': 2,
      'Full Access': 3,
    };

    // Build targeting rules array
    const targetingRules = [];

    // Add role rules from the roleRules array (user-managed)
    this.roleRules.forEach(roleRule => {
      targetingRules.push({
        ruleType: 'role',
        operator: 'in',
        targetValues: roleRule.role.split(', '), // Split in case multiple roles
        priority: roleRule.priority,
        isEnabled: true
      });
    });

    // Auto-add SuperUser rule ONLY in create mode (not edit mode)
    // In edit mode, backend/Quick Actions already added it, so respect user's choice
    if (!this.isEditMode && formValue.status === 'Limited Access') {
      const hasSuperUser = this.roleRules.some(r =>
        r.role.toLowerCase().includes('superuser')
      );

      if (!hasSuperUser) {
        targetingRules.push({
          ruleType: 'role',
          operator: 'in',
          targetValues: ['SuperUser'],
          priority: 100,  // High priority
          isEnabled: true
        });
      }
    }

    // Add specific users rule if any users are defined
    if (this.specificUsers.length > 0) {
      // Send each UserTarget as a JSON string in the array
      // Backend will serialize the entire array again for storage
      const userTargetStrings = this.specificUsers.map(u => JSON.stringify(u));
      targetingRules.push({
        ruleType: 'user',
        operator: 'in',
        targetValues: userTargetStrings, // Array of JSON strings, one per user
        priority: 10,
        isEnabled: true
      });
    }

    const payload = {
      featureKey: formValue.key,
      name: formValue.name,
      description: formValue.description,
      category: formValue.category,
      tags: [], // Empty array to maintain API compatibility
      statusId: statusIdMap[formValue.status as FeatureStatus] || 1, // Default to Disabled (1)
      rolloutPercentage: formValue.rolloutPercentage || 100, // Default 100% (not using gradual rollout currently)
      targetingRules: targetingRules, // Send targeting rules for both create and update
      version: this.currentVersion, // Include version for optimistic concurrency
    };

    const request$ = this.isEditMode && this.featureId
      ? this.featureService.updateFeature(this.featureId, payload)
      : this.featureService.createFeature(payload);

    request$.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.loading = false;
        this.featureFlagService.refreshAll();
        this.router.navigate(['/feature-management/features']);
      },
      error: (error) => {
        console.error('Error saving feature:', error);
        this.loading = false;

        // Extract error message
        const errorMessage = error?.error?.message || error?.message || 'Failed to save feature';

        // Show error toast (consistent with app-wide error handling)
        this.toastService.showToast({
          title: 'Error',
          subTitle: errorMessage,
          type: 'error',
        });

        // Reset scheduled fields in form if save failed (prevent UI from showing preview)
        if (this.isEditMode && this.featureId) {
          // Reload feature to reset form to actual database state
          this.loadFeature(this.featureId);
        }
      },
    });
  }

  cancel(): void {
    // Check if form has unsaved changes
    if (this.hasUnsavedChanges()) {
      const dialogData: ConfirmationDialogData = {
        title: 'Unsaved Changes',
        message: 'You have unsaved changes. Are you sure you want to leave? All changes will be lost.',
        confirmText: 'Leave',
        cancelText: 'Stay',
        confirmColor: 'warn'
      };

      this.dialog
        .open(ConfirmationDialogComponent, {
          width: '450px',
          data: dialogData,
        })
        .afterClosed()
        .subscribe((confirmed: boolean) => {
          if (confirmed) {
            this.router.navigate(['/feature-management/features']);
          }
        });
    } else {
      // No changes, navigate directly
      this.router.navigate(['/feature-management/features']);
    }
  }

  /**
   * Check if form has unsaved changes
   */
  hasUnsavedChanges(): boolean {
    if (!this.initialFormState) {
      return false;
    }

    const currentState = this.captureFormState();
    return JSON.stringify(this.initialFormState) !== JSON.stringify(currentState);
  }

  /**
   * Capture current form state including form values and additional data
   */
  private captureFormState(): any {
    return {
      formValues: this.featureForm.value,
      specificUsers: this.specificUsers.map(u => ({ ...u })),
      roleRules: this.roleRules.map(r => ({ ...r })),
    };
  }

  onRolloutPercentageChange(value: number): void {
    this.featureForm.patchValue({ rolloutPercentage: value });
  }

  private generateId(): string {
    return `rule_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private generateKeyFromName(name: string): string {
    // Convert "New Dashboard" to "NEW_DASHBOARD"
    return name
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '_') // Replace non-alphanumeric with underscore
      .replace(/^_+|_+$/g, ''); // Remove leading/trailing underscores
  }

  getFieldError(fieldName: string): string {
    const field = this.featureForm.get(fieldName);
    if (field?.hasError('required')) {
      return 'This field is required';
    }
    if (field?.hasError('pattern')) {
      return 'Must be SCREAMING_SNAKE_CASE (e.g., NEW_DASHBOARD)';
    }
    if (field?.hasError('min')) {
      return 'Must be at least 0';
    }
    if (field?.hasError('max')) {
      return 'Must be at most 100';
    }
    return '';
  }

  // User whitelist management
  addUser(): void {
    if (!this.selectedUserId) {
      return;
    }

    // Find the selected user
    const selectedUser = this.allUsers.find(u => u.id === this.selectedUserId);
    if (!selectedUser) {
      return;
    }

    // Check if user is already added
    if (this.specificUsers.some(u => u.userId === selectedUser.id)) {
      this.userFeedbackMessage = `${selectedUser.firstName} ${selectedUser.lastName} is already added`;
      this.userFeedbackType = 'warning';
      setTimeout(() => {
        this.userFeedbackMessage = '';
        this.userFeedbackType = '';
      }, 3000);
      return;
    }

    // Add user to the list
    this.specificUsers.push({
      userId: selectedUser.id,
      email: selectedUser.email
    });

    // Clear validation error since we now have targeting rules
    this.pilotValidationError = false;

    // Show success message
    this.userFeedbackMessage = `Added ${selectedUser.firstName} ${selectedUser.lastName}`;
    this.userFeedbackType = 'success';

    // Reset dropdown selection
    this.selectedUserId = null;

    // Auto-clear feedback after 3 seconds
    setTimeout(() => {
      this.userFeedbackMessage = '';
      this.userFeedbackType = '';
    }, 3000);
  }

  removeUser(userTarget: UserTarget): void {
    this.specificUsers = this.specificUsers.filter(u => u.userId !== userTarget.userId);
    const removedUser = this.allUsers.find(u => u.id === userTarget.userId);
    const displayName = removedUser ? `${removedUser.firstName} ${removedUser.lastName}` : userTarget.email;
    this.userFeedbackMessage = `Removed ${displayName}`;
    this.userFeedbackType = 'info';

    // Auto-clear feedback after 3 seconds
    setTimeout(() => {
      this.userFeedbackMessage = '';
      this.userFeedbackType = '';
    }, 3000);
  }

  getUserDisplayName(userTarget: UserTarget): string {
    const user = this.allUsers.find(u => u.id === userTarget.userId);
    return user ? `${user.firstName} ${user.lastName}` : userTarget.email;
  }

  addSuperUserRole(): void {
    // Check if SuperUser role already exists
    if (this.roleRules.some(r => r.role === 'SuperUser')) {
      this.roleFeedbackMessage = 'SuperUser role is already added';
      this.roleFeedbackType = 'warning';
      setTimeout(() => {
        this.roleFeedbackMessage = '';
        this.roleFeedbackType = '';
      }, 3000);
      return;
    }

    this.roleRules.push({
      role: 'SuperUser',
      priority: 100
    });

    this.roleFeedbackMessage = 'Added SuperUser role';
    this.roleFeedbackType = 'success';

    // Clear validation error since we now have targeting rules
    this.pilotValidationError = false;

    // Auto-clear feedback after 3 seconds
    setTimeout(() => {
      this.roleFeedbackMessage = '';
      this.roleFeedbackType = '';
    }, 3000);
  }

  hasSuperUserRole(): boolean {
    return this.roleRules.some(r => r.role === 'SuperUser');
  }

  isPilotMode(): boolean {
    return this.featureForm.get('status')?.value === 'Limited Access';
  }

  removeRole(role: string): void {
    this.roleRules = this.roleRules.filter(r => r.role !== role);
    this.roleFeedbackMessage = `Removed ${role} role`;
    this.roleFeedbackType = 'info';

    // Auto-clear feedback after 3 seconds
    setTimeout(() => {
      this.roleFeedbackMessage = '';
      this.roleFeedbackType = '';
    }, 3000);
  }

  toggleUsersSection(): void {
    this.isUsersExpanded = !this.isUsersExpanded;

    // Auto-scroll to show the "Add User" button when expanding
    if (this.isUsersExpanded) {
      setTimeout(() => {
        const addUserSection = document.querySelector('.add-user-section');
        if (addUserSection) {
          addUserSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300); // Wait for collapse animation to complete
    }
  }

  toggleRolesSection(): void {
    this.isRolesExpanded = !this.isRolesExpanded;

    // Auto-scroll to show the roles section when expanding
    if (this.isRolesExpanded) {
      setTimeout(() => {
        const rolesSection = document.querySelector('.roles-list, .no-roles, .add-superuser-section');
        if (rolesSection) {
          rolesSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);
    }
  }

  // Summary text methods for collapsed sections
  getUsersSummary(): string {
    if (this.specificUsers.length === 0) {
      return '';
    }
    return `(${this.specificUsers.length} user${this.specificUsers.length === 1 ? '' : 's'})`;
  }

  getRolesSummary(): string {
    if (this.roleRules.length === 0) {
      return '';
    }
    const roleNames = this.roleRules.map(r => r.role).join(', ');
    return `(${roleNames})`;
  }

  shouldShowRolesSection(): boolean {
    // Show roles section only if in edit mode AND has role rules
    return this.isEditMode && this.roleRules.length > 0;
  }
}
